import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/lib/api';
import { getStaticUrl } from '@/lib/api/client';
import { useFamilyData } from '@/lib/context/FamilyDataContext';
import { getPointsIcon } from '@/lib/utils/pointsIcon';

const avatarColors = ['#1565C0', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#0d9488', '#1850CC'];

interface NewChild {
  name: string;
  nickname: string;
  birthday: string;
  gender: number;
  school: string;
  class_name: string;
  avatar_type: 'color' | 'photo';
  avatar_color: string;
  avatar_url?: string;
}

export default function ChildrenTab() {
  const navigate = useNavigate();
  const { currentFamily } = useAuth();
  const { children, loading: apiLoading, refreshChildren } = useFamilyData(); // 从共享Context获取
  const pointsIcon = getPointsIcon(currentFamily?.point_name);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editChildId, setEditChildId] = useState<string | null>(null);
  const [isVip, setIsVip] = useState(currentFamily?.vip_status === 2);
  const [newChild, setNewChild] = useState<NewChild>({
    name: '', nickname: '', birthday: '', gender: 1,
    school: '', class_name: '', avatar_type: 'color', avatar_color: '#1565C0'
  });
  const [editChild, setEditChild] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const addFileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  const canManage = currentFamily?.my_role === 1;

  // 更新VIP状态
  useEffect(() => {
    setIsVip(currentFamily?.vip_status === 2);
  }, [currentFamily?.vip_status]);

  // 处理文件上传
  const handleFileUpload = async (file: File, isEdit: boolean = false) => {
    if (!file) return;

    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件');
      return;
    }

    // 检查文件大小（限制2MB）
    if (file.size > 2 * 1024 * 1024) {
      alert('图片大小不能超过2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target?.result as string;
      try {
        // 先上传到服务器获取URL
        const res = await api.settings.uploadAvatar(base64, file.name) as any;
        const url = res?.data?.url || res?.url;
        if (!url) {
          alert('头像上传失败：服务器未返回URL');
          return;
        }
        if (isEdit) {
          setEditChild({ ...editChild, avatar_url: url, avatar_type: 2 });
        } else {
          setNewChild({ ...newChild, avatar_url: url, avatar_type: 'photo' });
        }
      } catch (err) {
        console.error('头像上传失败:', err);
        alert('头像上传失败，请重试');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAddChild = () => {
    if (!isVip && children.length >= 1) {
      setShowVipModal(true);
      return;
    }
    setNewChild({
      name: '', nickname: '', birthday: '', gender: 1,
      school: '', class_name: '', avatar_type: 'color', avatar_color: '#1565C0'
    });
    setShowAddModal(true);
  };

  const handleEditClick = (e: React.MouseEvent, childId: string) => {
    e.stopPropagation();
    const child = children.find(c => c.id === childId);
    if (child) {
      setEditChild(child);
      setEditChildId(childId);
      setShowEditModal(true);
    }
  };

  const handleCardClick = (childId: string) => {
    navigate(`/analytics?child=${childId}`);
  };

  const handleSubmitAdd = async () => {
    if (!currentFamily?.id || !newChild.name) return;

    try {
      setSubmitting(true);
      const res = await api.family.createChild(currentFamily.id, {
        name: newChild.name,
        nickname: newChild.nickname || newChild.name,
        birthday: newChild.birthday || null,
        gender: newChild.gender,
        school: newChild.school || null,
        class_name: newChild.class_name || null,
        avatar_type: newChild.avatar_type === 'color' ? 1 : 2,
        avatar_color: newChild.avatar_color,
        avatar_url: newChild.avatar_url || null
      });

      if (res.code === 0) {
        setShowAddModal(false);
        // 刷新列表
        await refreshChildren();
      }
    } catch (error) {
      console.error('Failed to add child:', error);
      alert('添加失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!currentFamily?.id || !editChildId || !editChild) return;

    try {
      setSubmitting(true);
      const res = await api.family.updateChild(currentFamily.id, editChildId, {
        name: editChild.name,
        nickname: editChild.nickname || editChild.name,
        birthday: editChild.birthday || null,
        gender: editChild.gender,
        school: editChild.school || null,
        class_name: editChild.class_name || null,
        avatar_type: editChild.avatar_type || 1,
        avatar_color: editChild.avatar_color || '#3B82F6',
        avatar_url: editChild.avatar_type === 2 ? editChild.avatar_url : null
      });

      if (res.code === 0) {
        setShowEditModal(false);
        // 刷新列表
        await refreshChildren();
      }
    } catch (error) {
      console.error('Failed to update child:', error);
      alert('更新失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteChild = async (childId: string) => {
    if (!currentFamily?.id) return;
    if (!confirm('确定要删除这个孩子吗？')) return;

    try {
      const res = await api.family.deleteChild(currentFamily.id, childId);
      if (res.code === 0) {
        // 刷新列表
        await refreshChildren();
      }
    } catch (error) {
      console.error('Failed to delete child:', error);
      alert('删除失败，请重试');
    }
  };

  // 转换儿童数据用于显示
  const displayChildren = children.map(child => ({
    id: child.id,
    name: child.name,
    nickName: child.nickname || child.name,
    avatar: child.avatar_url && child.avatar_url !== ''
      ? getStaticUrl(child.avatar_url)
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(child.nickname || child.name || 'Child')}&background=${(child.avatar_color || '#3B82F6').replace('#', '')}&color=fff`,
    avatarBg: child.avatar_color || '#3B82F6',
    age: child.birthday ? Math.floor((Date.now() - new Date(child.birthday).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 6,
    gender: child.gender === 1 ? '男' : child.gender === 2 ? '女' : '未知',
    school: child.school || '',
    grade: child.class_name || '',
    totalPoints: child.total_points || 0,
    totalEarned: child.total_reward || 0,
    totalSpent: (child.total_punish || 0) + (child.total_exchange || 0),
    level: child.level?.level || 1,
    levelName: child.level?.name || '新手',
    levelProgress: child.level?.progress || 0,
    nextLevelName: '下一级',
    consecutiveCheckIn: child.checkin_days || 0
  }));

  if (apiLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-3xl text-blue-500 animate-spin mb-2"></i>
          <p className="text-gray-400 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="font-bold text-blue-900 text-sm flex items-center gap-1.5">
          <i className="ri-user-smile-fill text-blue-500"></i>
          孩子列表 ({children.length})
        </span>
        <button
          onClick={handleAddChild}
          disabled={!canManage}
          className={`flex items-center gap-1.5 text-xs px-4 py-2 rounded-xl transition-all whitespace-nowrap font-semibold ${
            canManage
              ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:opacity-90 cursor-pointer'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <i className="ri-add-line"></i>添加孩子
          {!canManage && <span className="text-[9px] ml-1">管理员</span>}
        </button>
      </div>

      {/* Children Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {displayChildren.map(child => {
          const levelPct = child.levelProgress;
          return (
            <div
              key={child.id}
              onClick={() => handleCardClick(child.id)}
              className="bg-white rounded-2xl p-5 cursor-pointer hover:-translate-y-1 transition-all border border-blue-100 hover:border-blue-300 relative group"
            >
              {/* Edit button */}
              <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => handleEditClick(e, child.id)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-50 text-blue-400 hover:bg-blue-100 hover:text-blue-600 transition-colors cursor-pointer"
                >
                  <i className="ri-edit-line text-sm"></i>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteChild(child.id); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-50 text-rose-400 hover:bg-rose-100 hover:text-rose-600 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>

              {/* Avatar + Name */}
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-shrink-0">
                  <img
                    src={child.avatar}
                    alt={child.name}
                    className="w-14 h-14 rounded-2xl object-cover object-top border-2 border-blue-100"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(child.nickname || child.name)}&background=3B82F6&color=fff`;
                    }}
                  />
                  <div
                    className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full border-2 border-white text-white text-[9px] font-black"
                    style={{ background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)' }}
                  >
                    {child.level}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="font-black text-blue-900 text-base">{child.name}</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full whitespace-nowrap font-medium border border-blue-100">{child.levelName}</span>
                  </div>
                  <p className="text-gray-400 text-xs">{child.age}岁 · {child.gender} · {child.grade}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <i className="ri-fire-fill text-amber-400 text-xs"></i>
                    <span className="text-xs text-amber-500 font-semibold">连续打卡 {child.consecutiveCheckIn} 天</span>
                  </div>
                </div>
              </div>

              {/* Points Stats */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-blue-50 rounded-xl p-2.5 text-center border border-blue-100">
                  <div className="flex items-center justify-center gap-0.5 mb-0.5">
                    <i className={`${pointsIcon} text-amber-400 text-xs`}></i>
                    <span className="text-blue-700 font-black text-sm leading-none">{child.totalPoints}</span>
                  </div>
                  <div className="text-gray-400 text-[10px] whitespace-nowrap">当前积分</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-2.5 text-center border border-emerald-100">
                  <div className="text-emerald-600 font-black text-sm leading-none mb-0.5">+{child.totalEarned}</div>
                  <div className="text-gray-400 text-[10px]">总获得</div>
                </div>
                <div className="bg-red-50 rounded-xl p-2.5 text-center border border-red-100">
                  <div className="text-red-500 font-black text-sm leading-none mb-0.5">-{child.totalSpent}</div>
                  <div className="text-gray-400 text-[10px]">总消耗</div>
                </div>
              </div>

              {/* Level Progress */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-500 font-medium">{child.levelName}</span>
                  <span className="text-xs text-blue-500 whitespace-nowrap">→ {child.nextLevelName}</span>
                </div>
                <div className="h-2.5 bg-blue-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${levelPct}%`,
                      background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)'
                    }}
                  ></div>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-gray-400">等级进度 {levelPct}%</span>
                  <span className="text-[10px] text-blue-500 font-medium cursor-pointer hover:underline flex items-center gap-0.5">
                    <i className="ri-line-chart-line text-[10px]"></i>查看分析
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Child Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[480px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-6 pt-6 pb-4 border-b border-gray-50 flex items-center justify-between z-10">
              <h3 className="font-bold text-blue-900 text-base flex items-center gap-2">
                <i className="ri-user-add-fill text-blue-500"></i>添加孩子
              </h3>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">头像设置</label>
                <div className="flex gap-2 mb-3">
                  {(['color', 'photo'] as const).map(t => (
                    <button
                      key={t}
                      onClick={() => setNewChild({ ...newChild, avatar_type: t })}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                        newChild.avatar_type === t ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-500'
                      }`}
                    >
                      {t === 'color' ? '色块头像' : '上传照片'}
                    </button>
                  ))}
                </div>
                {newChild.avatar_type === 'color' ? (
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-black text-lg flex-shrink-0"
                      style={{ background: newChild.avatar_color }}
                    >
                      {newChild.nickname || newChild.name || '孩'}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1.5">选择颜色</p>
                      <div className="flex gap-2 flex-wrap">
                        {avatarColors.map(color => (
                          <button
                            key={color}
                            onClick={() => setNewChild({ ...newChild, avatar_color: color })}
                            className={`w-6 h-6 rounded-full cursor-pointer transition-transform hover:scale-110 ${newChild.avatar_color === color ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : ''}`}
                            style={{ background: color }}
                          ></button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <input
                      ref={addFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, false);
                      }}
                    />
                    {newChild.avatar_url ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={getStaticUrl(newChild.avatar_url)}
                          alt="预览"
                          className="w-12 h-12 rounded-xl object-cover border-2 border-blue-100"
                        />
                        <div className="flex-1">
                          <button
                            onClick={() => addFileInputRef.current?.click()}
                            className="text-xs text-blue-500 hover:text-blue-700 cursor-pointer"
                          >
                            重新选择
                          </button>
                          <button
                            onClick={() => setNewChild({ ...newChild, avatar_url: undefined, avatar_type: 'color' })}
                            className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer ml-3"
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => addFileInputRef.current?.click()}
                        className="border-2 border-dashed border-blue-200 rounded-xl p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
                      >
                        <i className="ri-upload-cloud-2-fill text-2xl text-blue-200 mb-1"></i>
                        <p className="text-xs text-gray-400">点击上传照片</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">孩子姓名 <span className="text-red-400">*</span></label>
                  <input value={newChild.name} onChange={e => setNewChild({ ...newChild, name: e.target.value })} placeholder="请输入姓名"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">昵称</label>
                  <input value={newChild.nickname} onChange={e => setNewChild({ ...newChild, nickname: e.target.value })} placeholder="小名或昵称"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">生日</label>
                  <input type="date" value={newChild.birthday} onChange={e => setNewChild({ ...newChild, birthday: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">性别</label>
                  <div className="flex gap-2">
                    {[
                      { value: 1, label: '👦 男' },
                      { value: 2, label: '👧 女' }
                    ].map(g => (
                      <button key={g.value} onClick={() => setNewChild({ ...newChild, gender: g.value })}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                          newChild.gender === g.value ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-500'
                        }`}
                      >{g.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">学校</label>
                  <input value={newChild.school} onChange={e => setNewChild({ ...newChild, school: e.target.value })} placeholder="所在学校"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">班级</label>
                  <input value={newChild.class_name} onChange={e => setNewChild({ ...newChild, class_name: e.target.value })} placeholder="例：三年级2班"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
              </div>

              <button
                onClick={handleSubmitAdd}
                disabled={submitting || !newChild.name}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white py-3 rounded-xl font-bold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? '添加中...' : '确认添加'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Child Modal */}
      {showEditModal && editChild && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[400px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-6 pt-6 pb-4 border-b border-gray-50 flex items-center justify-between z-10">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className="ri-edit-fill text-blue-500"></i>编辑孩子信息
              </h3>
              <button onClick={() => setShowEditModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* 头像设置区域 */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">头像设置</label>
                <div className="flex gap-2 mb-3">
                  {(['color', 'photo'] as const).map(t => (
                    <button
                      key={t}
                      onClick={() => setEditChild({ ...editChild, avatar_type: t === 'color' ? 1 : 2 })}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                        (t === 'color' && editChild.avatar_type === 1) || (t === 'photo' && editChild.avatar_type === 2)
                          ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-500'
                      }`}
                    >
                      {t === 'color' ? '色块头像' : '上传照片'}
                    </button>
                  ))}
                </div>
                {editChild.avatar_type === 1 ? (
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-black text-lg flex-shrink-0"
                      style={{ background: editChild.avatar_color || '#3B82F6' }}
                    >
                      {editChild.nickname || editChild.name || '孩'}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1.5">选择颜色</p>
                      <div className="flex gap-2 flex-wrap">
                        {avatarColors.map(color => (
                          <button
                            key={color}
                            onClick={() => setEditChild({ ...editChild, avatar_color: color })}
                            className={`w-6 h-6 rounded-full cursor-pointer transition-transform hover:scale-110 ${
                              editChild.avatar_color === color ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : ''
                            }`}
                            style={{ background: color }}
                          ></button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <input
                      ref={editFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, true);
                      }}
                    />
                    {editChild.avatar_url ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={getStaticUrl(editChild.avatar_url)}
                          alt="预览"
                          className="w-12 h-12 rounded-xl object-cover border-2 border-blue-100"
                        />
                        <div className="flex-1">
                          <button
                            onClick={() => editFileInputRef.current?.click()}
                            className="text-xs text-blue-500 hover:text-blue-700 cursor-pointer"
                          >
                            重新选择
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => editFileInputRef.current?.click()}
                        className="border-2 border-dashed border-blue-200 rounded-xl p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
                      >
                        <i className="ri-upload-cloud-2-fill text-2xl text-blue-200 mb-1"></i>
                        <p className="text-xs text-gray-400">点击上传照片</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">姓名</label>
                  <input
                    value={editChild.name}
                    onChange={e => setEditChild({ ...editChild, name: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">昵称</label>
                  <input
                    value={editChild.nickname || ''}
                    onChange={e => setEditChild({ ...editChild, nickname: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">学校</label>
                  <input
                    value={editChild.school || ''}
                    onChange={e => setEditChild({ ...editChild, school: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">班级</label>
                  <input
                    value={editChild.class_name || ''}
                    onChange={e => setEditChild({ ...editChild, class_name: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEditModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
                <button
                  onClick={handleSubmitEdit}
                  disabled={submitting}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white text-sm font-bold cursor-pointer hover:opacity-90 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? '保存中...' : '保存修改'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIP Upgrade Modal */}
      {showVipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowVipModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[360px] p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 mx-auto mb-4">
              <i className="ri-vip-crown-fill text-white text-3xl"></i>
            </div>
            <h3 className="font-black text-blue-900 text-lg mb-2">免费版限制</h3>
            <p className="text-gray-500 text-sm mb-4">免费版最多管理 <strong>1位孩子</strong>，升级VIP可无限添加孩子！</p>
            <button className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-white py-3 rounded-xl font-bold cursor-pointer whitespace-nowrap mb-2">
              立即升级 VIP · 98元/年
            </button>
            <button onClick={() => setShowVipModal(false)} className="text-gray-400 text-sm cursor-pointer">暂不升级</button>
          </div>
        </div>
      )}
    </div>
  );
}
